<div class="h-full w-full flex flex-col overflow-y-auto scrollbar-transparent space-y-4">

    <div class="shrink-0">
        <h2 class="text-base/7 font-semibold text-primary">Agentic Brief Documentation</h2>
        <p class="text-sm/6 text-secondary">Follow the prompts below, we'll generate brief documentation for you.</p>
    </div>


    @if (reviewContentMode()) {

    <div
        class="flex-1 overflow-y-auto scrollbar-transparent border border-gray-200 dark:border-white/10 rounded-xl p-4">

        <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">

            <div class="sm:col-span-full">
                <label for="comment" class="block text-sm/6 font-medium text-primary">
                    Brief Overview / Key Messaging
                    <p class="text-xs text-secondary">Background information, applicable facts, main points and
                        concepts to be covered in the content.</p>
                </label>
                <div class="mt-2">
                    <atom-wysiswyg [inputHtml]="createBriefService.stepsHtml()" (htmlContentChange)="onHtmlContentChange($event, 'key')"
                        [placeholder]="'E.g. Our company is launching a new product line targeting Gen-Z consumers. We need content that highlights sustainability and the benefits of the new product line.'"></atom-wysiswyg>
                </div>
            </div>


            <div class="sm:col-span-full">
                <label for="comment" class="block text-sm/6 font-medium text-primary">
                    Mandatory Items
                    <p class="text-xs text-secondary">What must be included in the content?</p>
                </label>
                <div class="mt-2">
                    <atom-wysiswyg [inputHtml]="createBriefService.doHtml()" (htmlContentChange)="onHtmlContentChange($event, 'do')"
                        [placeholder]="'E.g. Product demonstration, specific talking points, logo/brand visibility...'"></atom-wysiswyg>
                </div>
            </div>

            <div class="sm:col-span-full">
                <label for="comment" class="block text-sm/6 font-medium text-primary">
                    Restrictions
                    <p class="text-xs text-secondary">What be avoided in the content?</p>
                </label>
                <div class="mt-2">
                    <atom-wysiswyg [inputHtml]="createBriefService.dontHtml()" (htmlContentChange)="onHtmlContentChange($event, 'dont')"
                        [placeholder]="'E.g. No foul language, no political content, no religious content, no direct competitor mentions...'"></atom-wysiswyg>
                </div>
            </div>
        </div>

    </div>


    } @else {
    <div
        class="flex-1 overflow-y-auto scrollbar-transparent border border-gray-200 dark:border-white/10 rounded-xl p-4">
        <!-- Chat Messages -->
        <div class="flex flex-col space-y-4">
            @for (message of messages(); track $index) {
            @if (message.isUser) {
            <!-- User Message -->
            <div class="flex items-start gap-x-3 justify-end">
                <!-- Message Bubble -->
                <div class="flex-1 max-w-[60%] bg-slate-200 dark:bg-slate-700 rounded-lg rounded-tr-none p-4">
                    @if (selectedQuickStartOption()) {
                    <button type="button"
                        class="flex flex-col items-start p-3 rounded-lg border border-gray-300 dark:border-gray-600 hover:border-primary dark:hover:border-primary hover:bg-primary/10 dark:hover:bg-primary/15 transition-colors text-left">
                        <div class="flex items-center gap-x-2 w-full">
                            <div class="size-3 rounded-full" [class]="selectedQuickStartOption()!.colour"></div>
                            <span class="text-sm font-semibold text-primary">{{ selectedQuickStartOption()!.name
                                }}</span>
                        </div>
                        <p class="mt-1 text-xs text-secondary">{{ selectedQuickStartOption()!.description }}</p>
                    </button>
                    } @else {
                    <p class="text-sm text-white whitespace-pre-wrap">
                        {{ message.displayedText }}
                    </p>
                    }
                </div>
                <!-- Message Bubble - END -->
                <!-- User Avatar -->
                <div class="shrink-0 size-8 rounded-full bg-gray-400 dark:bg-gray-600 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-5 text-white">
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                    </svg>
                </div>
                <!-- User Avatar - END -->
            </div>
            <!-- User Message - END -->
            } @else {
            <!-- AI Message -->
            <div class="flex items-start gap-x-3">
                <!-- AI Avatar -->
                <div class="shrink-0 size-8 rounded-full bg-primary flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-5 text-white">
                        <path d="M12 8V4H8" />
                        <rect width="16" height="12" x="4" y="8" rx="2" />
                        <path d="M2 14h2" />
                        <path d="M20 14h2" />
                        <path d="M15 13v2" />
                        <path d="M9 13v2" />
                    </svg>
                </div>
                <!-- Message Bubble -->
                <div class="flex-1 bg-gray-100 dark:bg-gray-800 rounded-lg rounded-tl-none p-4">
                    <p class="text-sm text-primary whitespace-pre-wrap">
                        <span>{{ message.displayedText }}</span>
                        @if (message.isTyping) {
                        <span class="inline-block w-2 h-4 bg-primary dark:bg-primary/75 animate-pulse ml-1">|</span>
                        }
                    </p>
                    <!-- Show quick start options after second message completes -->
                    @if ($index === 1 && message.isComplete && !message.isTyping && !hasUserSelectedOption()) {
                    <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        @for (option of quickStartOptions; track option.id) {
                        <button type="button" (click)="selectQuickStartOption(option)"
                            class="flex flex-col items-start p-3 rounded-lg border border-gray-300 dark:border-gray-600 hover:border-primary dark:hover:border-primary hover:bg-primary/10 dark:hover:bg-primary/15 transition-colors text-left cursor-pointer">
                            <div class="flex items-center gap-x-2 w-full">
                                <div class="size-3 rounded-full" [class]="option.colour"></div>
                                <span class="text-sm font-semibold text-primary">{{ option.name }}</span>
                            </div>
                            <p class="mt-1 text-xs text-secondary">{{ option.description }}</p>
                        </button>
                        }
                    </div>
                    }
                </div>
            </div>
            <!-- AI Message - END -->
            }
            }
        </div>
        <!-- Chat Messages - END -->
    </div>
    }

    @if (showMessageInput()) {
    <div class="shrink-0 px-2 pb-2">
        <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-primary">Or type your own answer:</label>
            <div class="flex flex-row items-end gap-x-2">
                <textarea rows="2" placeholder="Type your message here..."
                    class="flex-1 resize-none input-textarea text-primary" [(ngModel)]="userInput"></textarea>
                <!-- <button type="button" class="fab-button-primary cursor-pointer"
                    [disabled]="!userInput || userInput.trim().length === 0" (click)="sendUserMessage()"> -->
                <button type="button" class="fab-button-primary cursor-pointer"
                    [disabled]="!userInput || userInput.trim().length === 0" (click)="sendTypeQuickStartAnswer()">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-5">
                        <path
                            d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z" />
                        <path d="M6 12h16" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
    }

</div>